import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Handshake,
  Download,
  FileText,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Building2,
  Crown,
  Users,
  Briefcase,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  {
    name: "Individual",
    range: "$250",
    color: "border-neutral-700 text-neutral-200",
    chip: "bg-neutral-200 text-black",
    accent: "text-neutral-300",
    perks: [
      "Founding member card",
      "Name listed on the AfroAm supporter wall",
      "Two complimentary showcase tickets",
      "Quarterly field-report newsletter",
    ],
  },
  {
    name: "Community Partner",
    range: "$1,000",
    color: "border-neutral-500 text-neutral-100",
    chip: "bg-neutral-300 text-black",
    accent: "text-neutral-200",
    perks: [
      "Logo on community event signage",
      "Social media partner shout-out",
      "Four complimentary showcase tickets",
      "Featured mention in newsletter",
    ],
  },
  {
    name: "Bronze",
    range: "$2,500",
    color: "border-amber-700/50 text-amber-200",
    chip: "bg-amber-700 text-amber-50",
    accent: "text-amber-200",
    perks: [
      "Logo on jerseys and event banners",
      "Quarterly newsletter feature",
      "Showcase booth space + 6 tickets",
      "Co-branded social content",
    ],
  },
  {
    name: "Silver",
    range: "$5,000",
    color: "border-zinc-300/50 text-zinc-100",
    chip: "bg-zinc-200 text-black",
    accent: "text-zinc-100",
    perks: [
      "All Bronze benefits",
      "Logo on broadcast lower-thirds",
      "Half-page feature in event program",
      "Dedicated media segment / interview",
      "10 VIP tickets + reserved seating",
    ],
  },
  {
    name: "Gold",
    range: "$10,000",
    color: "border-amber-400/60 text-amber-200",
    chip: "bg-amber-400 text-black",
    accent: "text-amber-300",
    perks: [
      "All Silver benefits",
      "Naming rights on a league night",
      "Premium courtside signage",
      "20 VIP tickets + hospitality suite",
      "Dedicated press release",
      "Executive board meet & greet",
    ],
  },
  {
    name: "Founders Title",
    range: "$25,000",
    color: "border-red-500/60 text-red-200",
    chip: "bg-red-500 text-white",
    accent: "text-red-300",
    perks: [
      "Title naming rights — entire season",
      "All Gold benefits",
      "Equity & team-ownership conversation eligibility",
      "Full league integration (apparel, courts, comms)",
      "Dedicated PR & creative team",
      "Founders crest on permanent assets",
      "Custom international activation tour",
    ],
  },
];

const ORG_TYPES = [
  "Corporate Sponsor",
  "Community Organization",
  "Government Agency",
  "Nonprofit Organization",
  "Sports Organization",
  "Media Partner",
  "Investor Group",
  "Educational Institution",
  "Other",
];

const INTERESTS = [
  "Corporate Sponsorship",
  "Team Ownership Opportunities",
  "Event Sponsorship & Activation",
  "Youth Development Programs",
  "Community Outreach Programs",
  "Facility & Venue Partnership",
  "Investment Frameworks",
  "Athlete Development Programs",
  "NIL & Brand Partnerships",
  "Media, Production & Broadcasting",
  "Basketball Camps & Clinics",
  "International Basketball Development",
  "Education & Scholarship Programs",
];

const CONTRIBUTIONS = [
  "Financial Sponsorship",
  "Marketing & PR Support",
  "Transportation Assets",
  "Volunteer Staffing",
  "Housing Accommodations",
  "Professional Services",
  "Facilities / Venues",
  "Food & Beverage Support",
  "Educational Resources",
  "Equipment / Apparel",
  "Media & Press Coverage",
  "Technology / IT Support",
];

const VALUE_RANGES = [
  "Individual — $250",
  "Community Partner — $1,000",
  "Bronze — $2,500",
  "Silver — $5,000",
  "Gold — $10,000",
  "Founders Title — $25,000",
  "Custom / Above $25,000",
];

const TERMS = [
  "Event-Based Only",
  "6 Months",
  "1 Year",
  "Multi-Year Term",
  "Long-Term Strategic",
];

const EXEC_BOARD = [
  { name: "Michael Lewis", role: "President of Sports Operations" },
  { name: "Justin Allen", role: "Vice President & CFO" },
  { name: "Javan Smith", role: "Director of Operations" },
];

const inputCls =
  "w-full bg-transparent border-b border-neutral-700 focus:border-amber-400 text-white py-3 outline-none placeholder:text-neutral-700 transition-colors text-sm";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-neutral-400";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left text-xs leading-tight px-3.5 py-2.5 border transition-colors ${
      active
        ? "border-amber-400 bg-amber-400/10 text-white"
        : "border-neutral-800 text-neutral-400 hover:border-neutral-600 hover:text-neutral-200"
    }`}
  >
    {children}
  </button>
);

const PartnershipPage = () => {
  const [form, setForm] = useState({
    organization_name: "",
    organization_type: "",
    website: "",
    primary_contact: "",
    title: "",
    email: "",
    phone: "",
    business_address: "",
    city: "",
    state: "",
    country: "",
    interest_areas: [],
    contributions: [],
    strategic_goals: "",
    desired_outcomes: "",
    partnership_value: "",
    community_impact: "",
    target_start_date: "",
    preferred_term: "",
    supplementary_notes: "",
    authorized_signature: "",
    signer_title: "",
    signature_date: new Date().toISOString().slice(0, 10),
    agree_to_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleListItem = (k, item) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(item) ? f[k].filter((i) => i !== item) : [...f[k], item],
    }));

  const submit = async (e) => {
    e.preventDefault();
    const required = {
      organization_name: "Organization Name",
      organization_type: "Organization Type",
      primary_contact: "Primary Contact",
      email: "Email",
      phone: "Phone",
      partnership_value: "Partnership Value",
      preferred_term: "Preferred Term",
      authorized_signature: "Authorized Signature",
    };
    for (const [k, label] of Object.entries(required)) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please complete: ${label}.`);
        return;
      }
    }
    if (!form.agree_to_terms) {
      toast.error("Please confirm the certification to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/partnership`, form);
      toast.success("Submission received. We'll be in touch.");
      setSubmitted(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(
    () => [
      { icon: Crown, label: "6 Partnership Tiers", sub: "From Individual to Founders" },
      { icon: Users, label: "Executive Review", sub: "Michael · Justin · Javan" },
      { icon: Briefcase, label: "Flexible Terms", sub: "Event-Based → Long-Term" },
      { icon: TrendingUp, label: "Two Leagues", sub: "AAMBL · WAABL + Showcases" },
    ],
    []
  );

  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-14 border-b border-neutral-900 bg-gradient-to-b from-[#0a0a0a] to-black overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-amber-500/12 blur-[140px] pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-red-500/10 blur-[140px] pointer-events-none" />

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6">
            <span className="w-8 h-px bg-amber-400" />
            <Handshake size={14} />
            <span>Partnerships & Sponsorships</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            data-testid="partnership-title"
            className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] text-white"
          >
            Build with
            <br />
            <span className="text-amber-400">AfroAm.</span>
          </motion.h1>
          <p className="text-neutral-400 max-w-2xl mt-6 font-light leading-relaxed">
            Building Opportunity. Developing Talent. Transforming Communities
            Through Basketball. Align your brand with the AfroAm Basketball
            Association — explore the partnership tiers below, then submit the
            executive review form.
          </p>

          <div className="flex flex-wrap gap-3 mt-8">
            <a
              href="#tiers"
              data-testid="jump-tiers"
              className="inline-flex items-center gap-2 bg-amber-400 text-black px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
            >
              View Tiers
              <ArrowRight size={14} />
            </a>
            <a
              href="#form"
              data-testid="jump-form"
              className="inline-flex items-center gap-2 border border-white text-white px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
            >
              Apply Now
            </a>
            <a
              href="/docs/AfroAm_Partnership_Form.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-form-pdf"
              className="inline-flex items-center gap-2 border border-amber-400/40 text-amber-300 px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-400 hover:text-black transition-colors"
            >
              <Download size={14} />
              PDF Form
            </a>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-b border-neutral-900 bg-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-neutral-900">
          {stats.map((s) => (
            <div key={s.label} className="py-6 px-2 md:px-6 flex items-center gap-4">
              <s.icon size={18} className="text-amber-400 shrink-0" />
              <div>
                <div className="text-sm text-white uppercase tracking-wider">{s.label}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                  {s.sub}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tiers */}
      <section id="tiers" className="py-16 md:py-24 border-b border-neutral-900">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
                — Tier Breakdown
              </div>
              <h2
                data-testid="tiers-title"
                className="font-display uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-white"
              >
                Pick your
                <br />
                <span className="text-amber-400">altitude.</span>
              </h2>
            </div>
            <p className="text-neutral-400 max-w-sm font-light leading-relaxed">
              Activation packages scale with investment. Every tier is fully
              customizable through the executive board.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tier-grid">
            {TIERS.map((t, i) => (
              <motion.article
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.06, duration: 0.5 }}
                data-testid={`tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative border ${t.color} bg-[#0a0a0a] p-6 md:p-7 flex flex-col`}
              >
                <div className={`absolute top-4 right-4 ${t.chip} text-[9px] uppercase tracking-[0.3em] font-bold px-2.5 py-1`}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className={`text-[10px] uppercase tracking-[0.35em] ${t.accent} mb-3`}>
                  Tier {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display uppercase text-3xl md:text-4xl text-white leading-tight">
                  {t.name}
                </h3>
                <div className={`font-display text-2xl mt-2 ${t.accent}`}>
                  {t.range}
                </div>
                <ul className="mt-6 space-y-2 text-sm text-neutral-300 flex-1">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className={t.accent}>▸</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#form"
                  className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white hover:text-amber-400 transition-colors"
                  data-testid={`tier-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  Apply for this tier
                  <ArrowRight size={12} />
                </a>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Submission Form */}
      <section
        id="form"
        className="py-16 md:py-24 border-b border-neutral-900 bg-[#070707]"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative border border-amber-400/40 bg-[#0a0a0a] p-8 md:p-12"
              data-testid="partnership-success"
            >
              <span className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-amber-400" />
              <span className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-amber-400" />
              <span className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-amber-400" />
              <span className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-amber-400" />
              <CheckCircle2 size={42} className="text-amber-400 mb-6" />
              <h2 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.95]">
                Submission
                <br />
                <span className="text-amber-400">received.</span>
              </h2>
              <p className="text-neutral-300 mt-6 leading-relaxed max-w-2xl">
                Thank you,{" "}
                <span className="text-white">{submitted.organization_name}</span>.
                Our executive leadership team will review your{" "}
                <span className="text-white">{submitted.partnership_value}</span>{" "}
                ({submitted.preferred_term}) proposal and reach out at{" "}
                <span className="text-white">{submitted.email}</span> with next
                steps.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/"
                  data-testid="partnership-back-home"
                  className="inline-flex items-center gap-2 border border-white text-white px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                >
                  Back to Home
                </Link>
                <a
                  href="mailto:afroambasketball@gmail.com?subject=Partnership%20Follow-Up"
                  data-testid="partnership-email-followup"
                  className="inline-flex items-center gap-2 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                >
                  Email Follow-Up
                </a>
              </div>
            </motion.div>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
                — Executive Submission Form
              </div>
              <h2
                data-testid="form-title"
                className="font-display uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-white"
              >
                Submit for
                <br />
                <span className="text-amber-400">review.</span>
              </h2>

              <form onSubmit={submit} data-testid="partnership-form" className="mt-12 space-y-12">
                {/* Section 1: Organization */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-6">
                    Step 01 · Organization Profile
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className={labelCls}>Organization Name *</label>
                      <input
                        className={inputCls}
                        value={form.organization_name}
                        onChange={(e) => setField("organization_name", e.target.value)}
                        data-testid="p-org-name"
                        required
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Organization Type *</label>
                      <select
                        className={`${inputCls} appearance-none [color-scheme:dark]`}
                        value={form.organization_type}
                        onChange={(e) => setField("organization_type", e.target.value)}
                        data-testid="p-org-type"
                        required
                      >
                        <option value="">Select type</option>
                        {ORG_TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Website / URL</label>
                      <input
                        className={inputCls}
                        value={form.website}
                        onChange={(e) => setField("website", e.target.value)}
                        data-testid="p-website"
                        placeholder="https://"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Contact */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-6">
                    Step 02 · Primary Contact
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>Full Name *</label>
                      <input
                        className={inputCls}
                        value={form.primary_contact}
                        onChange={(e) => setField("primary_contact", e.target.value)}
                        data-testid="p-contact-name"
                        required
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Title</label>
                      <input
                        className={inputCls}
                        value={form.title}
                        onChange={(e) => setField("title", e.target.value)}
                        data-testid="p-contact-title"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Email *</label>
                      <input
                        type="email"
                        className={inputCls}
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        data-testid="p-email"
                        required
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Phone *</label>
                      <input
                        type="tel"
                        className={inputCls}
                        value={form.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        data-testid="p-phone"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelCls}>Business Address</label>
                      <input
                        className={inputCls}
                        value={form.business_address}
                        onChange={(e) => setField("business_address", e.target.value)}
                        data-testid="p-address"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>City</label>
                      <input
                        className={inputCls}
                        value={form.city}
                        onChange={(e) => setField("city", e.target.value)}
                        data-testid="p-city"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>State / Province</label>
                      <input
                        className={inputCls}
                        value={form.state}
                        onChange={(e) => setField("state", e.target.value)}
                        data-testid="p-state"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Country</label>
                      <input
                        className={inputCls}
                        value={form.country}
                        onChange={(e) => setField("country", e.target.value)}
                        data-testid="p-country"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Interest areas */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-3">
                    Step 03 · Areas of Interest
                  </div>
                  <p className="text-xs text-neutral-500 mb-5">
                    Select all that apply.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="p-interests">
                    {INTERESTS.map((i) => (
                      <Chip
                        key={i}
                        active={form.interest_areas.includes(i)}
                        onClick={() => toggleListItem("interest_areas", i)}
                        testid={`int-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      >
                        {i}
                      </Chip>
                    ))}
                  </div>
                </div>

                {/* Section 4: Contributions */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-3">
                    Step 04 · What You Bring
                  </div>
                  <p className="text-xs text-neutral-500 mb-5">
                    Assets, capabilities, or provisions your organization can contribute.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="p-contributions">
                    {CONTRIBUTIONS.map((i) => (
                      <Chip
                        key={i}
                        active={form.contributions.includes(i)}
                        onClick={() => toggleListItem("contributions", i)}
                        testid={`con-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      >
                        {i}
                      </Chip>
                    ))}
                  </div>
                </div>

                {/* Section 5: Goals & narrative */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-6">
                    Step 05 · Strategic Alignment
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className={labelCls}>
                        Your core strategic goals for partnering with AfroAm
                      </label>
                      <textarea
                        rows={3}
                        className={`${inputCls} resize-none`}
                        value={form.strategic_goals}
                        onChange={(e) => setField("strategic_goals", e.target.value)}
                        data-testid="p-goals"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>
                        Explicit operational, commercial, or structural outcomes desired
                      </label>
                      <textarea
                        rows={3}
                        className={`${inputCls} resize-none`}
                        value={form.desired_outcomes}
                        onChange={(e) => setField("desired_outcomes", e.target.value)}
                        data-testid="p-outcomes"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>
                        How your ecosystem supports community development, youth
                        empowerment, healthcare, wellness, or sports development
                      </label>
                      <textarea
                        rows={3}
                        className={`${inputCls} resize-none`}
                        value={form.community_impact}
                        onChange={(e) => setField("community_impact", e.target.value)}
                        data-testid="p-impact"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 6: Value & term */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-6">
                    Step 06 · Value & Term
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>
                        Estimated Tier or Annual Partnership Value *
                      </label>
                      <select
                        className={`${inputCls} appearance-none [color-scheme:dark]`}
                        value={form.partnership_value}
                        onChange={(e) => setField("partnership_value", e.target.value)}
                        data-testid="p-value"
                        required
                      >
                        <option value="">Select range</option>
                        {VALUE_RANGES.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Preferred Terms / Duration *</label>
                      <select
                        className={`${inputCls} appearance-none [color-scheme:dark]`}
                        value={form.preferred_term}
                        onChange={(e) => setField("preferred_term", e.target.value)}
                        data-testid="p-term"
                        required
                      >
                        <option value="">Select term</option>
                        {TERMS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Target Commencement Date</label>
                      <input
                        type="date"
                        className={`${inputCls} [color-scheme:dark]`}
                        value={form.target_start_date}
                        onChange={(e) => setField("target_start_date", e.target.value)}
                        data-testid="p-start-date"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelCls}>
                        Supplementary context, historical metrics, or operational data
                      </label>
                      <textarea
                        rows={3}
                        className={`${inputCls} resize-none`}
                        value={form.supplementary_notes}
                        onChange={(e) => setField("supplementary_notes", e.target.value)}
                        data-testid="p-notes"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 7: Signature */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-6">
                    Step 07 · Authorized Signature
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className={labelCls}>Signed By *</label>
                      <input
                        className={inputCls}
                        value={form.authorized_signature}
                        onChange={(e) => setField("authorized_signature", e.target.value)}
                        data-testid="p-signature"
                        placeholder="Full Name"
                        required
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Print Title</label>
                      <input
                        className={inputCls}
                        value={form.signer_title}
                        onChange={(e) => setField("signer_title", e.target.value)}
                        data-testid="p-signer-title"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Date</label>
                      <input
                        type="date"
                        className={`${inputCls} [color-scheme:dark]`}
                        value={form.signature_date}
                        onChange={(e) => setField("signature_date", e.target.value)}
                        data-testid="p-signature-date"
                      />
                    </div>
                  </div>

                  <label
                    className="flex items-start gap-3 text-xs text-neutral-300 leading-relaxed cursor-pointer mt-8"
                    data-testid="p-terms"
                  >
                    <input
                      type="checkbox"
                      checked={form.agree_to_terms}
                      onChange={(e) => setField("agree_to_terms", e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-amber-400"
                    />
                    <span>
                      I certify that all corporate data, financial valuations, and
                      operational capabilities specified across this form are
                      accurate, genuine, and authorized by my organization&apos;s
                      governing board.
                    </span>
                  </label>
                </div>

                <div className="pt-6 border-t border-neutral-900 flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="p-submit"
                    className="inline-flex items-center gap-3 bg-amber-400 text-black px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors disabled:opacity-60"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={14} />
                    ) : (
                      <ArrowRight size={14} />
                    )}
                    Submit for Executive Review
                  </button>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    Reviewed by AfroAm Executive Board
                  </span>
                </div>
              </form>
            </>
          )}
        </div>
      </section>

      {/* PDF + Executive Board */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
              — Official Form (PDF)
            </div>
            <h2 className="font-display uppercase text-3xl md:text-5xl text-white leading-[0.95] mb-6">
              Prefer the PDF version?
            </h2>
            <p className="text-neutral-400 max-w-xl mb-6 font-light leading-relaxed">
              Download the official Partnership & Sponsorship Submission Form,
              complete it offline, and return signed copies to the executive
              board.
            </p>

            <div className="relative border border-amber-400/30 bg-[#050505] aspect-[8.5/11] md:aspect-[8.5/8] overflow-hidden">
              <span className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-amber-400 z-10" />
              <span className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-amber-400 z-10" />
              <span className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-amber-400 z-10" />
              <span className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-amber-400 z-10" />
              <iframe
                src="/docs/AfroAm_Partnership_Form.pdf#view=FitH"
                title="AfroAm Partnership & Sponsorship Form"
                className="w-full h-full bg-white"
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3 justify-between text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              <span>AfroAm Basketball Association · Partnership v2</span>
              <a
                href="/docs/AfroAm_Partnership_Form.pdf"
                download="AfroAm_Partnership_Form.pdf"
                data-testid="pdf-direct-download"
                className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300"
              >
                <Download size={12} />
                Direct Download
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-neutral-900 bg-[#0a0a0a] p-7 md:p-9 sticky top-28">
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-4">
                — Executive Board
              </div>
              <h3 className="font-display uppercase text-2xl md:text-3xl text-white leading-tight">
                Reviewed by leadership.
              </h3>
              <ul className="mt-6 space-y-5">
                {EXEC_BOARD.map((p) => (
                  <li
                    key={p.name}
                    className="flex items-start gap-3 pb-5 border-b border-neutral-900 last:border-b-0 last:pb-0"
                  >
                    <Crown size={16} className="text-amber-400 mt-1 shrink-0" />
                    <div>
                      <div className="font-display text-xl text-white uppercase">
                        {p.name}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-0.5">
                        {p.role}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-6 border-t border-neutral-900 space-y-3 text-sm text-neutral-300">
                <div className="flex items-start gap-3">
                  <Building2 size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-0.5">
                      Home Headquarters
                    </div>
                    Las Vegas, Nevada
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-0.5">
                      Southwest Office
                    </div>
                    708 Main Street, Suite 1000
                    <br />
                    Houston, Texas 77002
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-0.5">
                      Pacific Division Office
                    </div>
                    470 Noor Street, Suite B
                    <br />
                    South San Francisco, California 94080
                  </div>
                </div>
                <a
                  href="mailto:afroambasketball@gmail.com?subject=Partnership%20Inquiry"
                  data-testid="exec-email"
                  className="block text-white hover:text-amber-400"
                >
                  afroambasketball@gmail.com
                </a>
                <a
                  href="tel:8323173208"
                  data-testid="exec-phone"
                  className="block text-white hover:text-amber-400"
                >
                  (832) 317-3208
                </a>
                <a
                  href="https://afroamhoopsglobal.us"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="exec-web"
                  className="block text-white hover:text-amber-400"
                >
                  afroamhoopsglobal.us
                </a>
              </div>

              <div className="mt-8 pt-6 border-t border-neutral-900 flex flex-wrap gap-3">
                <Link
                  to="/budget"
                  data-testid="link-budget"
                  className="inline-flex items-center gap-2 border border-white text-white px-5 py-3 text-[10px] uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                >
                  View Budget
                  <FileText size={12} />
                </Link>
                <a
                  href="#form"
                  data-testid="exec-apply-cta"
                  className="inline-flex items-center gap-2 bg-amber-400 text-black px-5 py-3 text-[10px] uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                >
                  Apply Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnershipPage;
